import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Palette, Shuffle, Download, Pipette } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface ColorValues {
  hex: string;
  rgb: { r: number; g: number; b: number };
  hsl: { h: number; s: number; l: number };
  hsv: { h: number; s: number; v: number };
  cmyk: { c: number; m: number; y: number; k: number };
}

export const ColorConverterTool: React.FC = () => {
  const { t } = useTranslation();
  const [color, setColor] = useState<ColorValues>({
    hex: '#3B82F6',
    rgb: { r: 59, g: 130, b: 246 },
    hsl: { h: 217, s: 91, l: 60 },
    hsv: { h: 217, s: 76, v: 96 },
    cmyk: { c: 76, m: 47, y: 0, k: 4 },
  });
  const [colorHistory, setColorHistory] = useState<string[]>(['#3B82F6']);
  const [savedPalette, setSavedPalette] = useState<string[]>([]);

  // Helper functions
  const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  const hexToHsl = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const rgbToHsv = (r: number, g: number, b: number): { h: number; s: number; v: number } => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, v = max;

    const d = max - min;
    s = max === 0 ? 0 : d / max;

    if (max === min) {
      h = 0;
    } else {
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      v: Math.round(v * 100)
    };
  };

  const rgbToCmyk = (r: number, g: number, b: number): { c: number; m: number; y: number; k: number } => {
    r /= 255;
    g /= 255;
    b /= 255;

    const k = 1 - Math.max(r, Math.max(g, b));
    const c = (1 - r - k) / (1 - k) || 0;
    const m = (1 - g - k) / (1 - k) || 0;
    const y = (1 - b - k) / (1 - k) || 0;

    return {
      c: Math.round(c * 100),
      m: Math.round(m * 100),
      y: Math.round(y * 100),
      k: Math.round(k * 100)
    };
  };

  const updateColorFromHex = (hex: string) => {
    const rgb = hexToRgb(hex);
    if (rgb) {
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
      const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
      
      setColor({ hex, rgb, hsl, hsv, cmyk });
    }
  };

  const updateColorFromRgb = (r: number, g: number, b: number) => {
    const hex = rgbToHex(r, g, b);
    const hsl = rgbToHsl(r, g, b);
    const hsv = rgbToHsv(r, g, b);
    const cmyk = rgbToCmyk(r, g, b);
    
    setColor({ hex, rgb: { r, g, b }, hsl, hsv, cmyk });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const addToHistory = (hexColor: string) => {
    setColorHistory(prev => {
      const newHistory = [hexColor, ...prev.filter(c => c !== hexColor)];
      return newHistory.slice(0, 20); // Keep last 20 colors
    });
  };

  const addToPalette = (hexColor: string) => {
    setSavedPalette(prev => {
      if (!prev.includes(hexColor)) {
        return [...prev, hexColor];
      }
      return prev;
    });
    toast.success(t('tools.colorConverter.colorAddedToPalette'));
  };

  const removeFromPalette = (hexColor: string) => {
    setSavedPalette(prev => prev.filter(c => c !== hexColor));
  };

  const generateRandomColor = () => {
    const randomHex = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    updateColorFromHex(randomHex);
    addToHistory(randomHex);
  };

  const generateComplementaryColor = () => {
    const h = (color.hsl.h + 180) % 360;
    const complementary = hslToHex(h, color.hsl.s, color.hsl.l);
    updateColorFromHex(complementary);
    addToHistory(complementary);
  };

  const generateAnalogousColors = () => {
    const colors = [];
    for (let i = -30; i <= 30; i += 15) {
      if (i !== 0) {
        const h = (color.hsl.h + i + 360) % 360;
        colors.push(hslToHex(h, color.hsl.s, color.hsl.l));
      }
    }
    return colors;
  };

  const generateTriadicColors = () => {
    const colors = [];
    for (let i = 120; i <= 240; i += 120) {
      const h = (color.hsl.h + i) % 360;
      colors.push(hslToHex(h, color.hsl.s, color.hsl.l));
    }
    return colors;
  };

  const exportPalette = () => {
    const paletteData = {
      name: t('tools.colorConverter.colorPalette'),
      colors: savedPalette.map(hex => ({
        hex,
        rgb: hexToRgb(hex),
        hsl: hexToHsl(hex)
      })),
      created: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(paletteData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-palette.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const presetColors = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#FFA500', '#800080', '#FFC0CB', '#A52A2A', '#808080', '#000000',
    '#FFFFFF', '#F0F8FF', '#FAEBD7', '#7FFFD4', '#F5F5DC', '#FFE4C4',
  ];

  return (
    <div className="space-y-6">
      {/* Color Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="w-5 h-5" />
            <span>{t('tools.colorConverter.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <div 
                className="w-full h-32 rounded-lg border-2 border-gray-300 dark:border-gray-600 shadow-inner"
                style={{ backgroundColor: color.hex }}
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={generateRandomColor}
                  variant="outline"
                  size="sm"
                >
                  <Shuffle className="w-4 h-4 mr-2" />
                  {t('tools.colorConverter.random')}
                </Button>
                <Button
                  onClick={() => copyToClipboard(color.hex)}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('tools.colorConverter.copyHex')}
                </Button>
                <Button
                  onClick={() => addToPalette(color.hex)}
                  variant="outline"
                  size="sm"
                >
                  <Palette className="w-4 h-4 mr-2" />
                  {t('tools.colorConverter.addToPalette')}
                </Button>
                <Button
                  onClick={generateComplementaryColor}
                  variant="outline"
                  size="sm"
                >
                  <Pipette className="w-4 h-4 mr-2" />
                  {t('tools.colorConverter.complementary')}
                </Button>
              </div>
            </div>
            
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                {t('tools.colorConverter.presetColors')}
              </h4>
              <div className="grid grid-cols-6 gap-2">
                {presetColors.map((presetColor) => (
                  <button
                    key={presetColor}
                    onClick={() => updateColorFromHex(presetColor)}
                    className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                    style={{ backgroundColor: presetColor }}
                    title={presetColor}
                  />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Values */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* HEX */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.colorConverter.hex')}</span>
              <Button
                onClick={() => copyToClipboard(color.hex)}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={color.hex}
              onChange={(e) => {
                const value = e.target.value;
                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                  if (value.length === 7) {
                    updateColorFromHex(value);
                  } else {
                    setColor(prev => ({ ...prev, hex: value }));
                  }
                }
              }}
              className="font-mono"
              placeholder="#000000"
            />
          </CardContent>
        </Card>

        {/* RGB */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.colorConverter.rgb')}</span>
              <Button
                onClick={() => copyToClipboard(`rgb(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})`)}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Input
                  label="R"
                  type="number"
                  min="0"
                  max="255"
                  value={color.rgb.r}
                  onChange={(e) => {
                    const r = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                    updateColorFromRgb(r, color.rgb.g, color.rgb.b);
                  }}
                />
                <Input
                  label="G"
                  type="number"
                  min="0"
                  max="255"
                  value={color.rgb.g}
                  onChange={(e) => {
                    const g = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                    updateColorFromRgb(color.rgb.r, g, color.rgb.b);
                  }}
                />
                <Input
                  label="B"
                  type="number"
                  min="0"
                  max="255"
                  value={color.rgb.b}
                  onChange={(e) => {
                    const b = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                    updateColorFromRgb(color.rgb.r, color.rgb.g, b);
                  }}
                />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                rgb({color.rgb.r}, {color.rgb.g}, {color.rgb.b})
              </div>
            </div>
          </CardContent>
        </Card>

        {/* HSL */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.colorConverter.hsl')}</span>
              <Button
                onClick={() => copyToClipboard(`hsl(${color.hsl.h}, ${color.hsl.s}%, ${color.hsl.l}%)`)}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Input
                  label="H"
                  type="number"
                  min="0"
                  max="360"
                  value={color.hsl.h}
                  readOnly
                />
                <Input
                  label="S"
                  type="number"
                  min="0"
                  max="100"
                  value={color.hsl.s}
                  readOnly
                />
                <Input
                  label="L"
                  type="number"
                  min="0"
                  max="100"
                  value={color.hsl.l}
                  readOnly
                />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                hsl({color.hsl.h}, {color.hsl.s}%, {color.hsl.l}%)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* HSV */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.colorConverter.hsv')}</span>
              <Button
                onClick={() => copyToClipboard(`hsv(${color.hsv.h}, ${color.hsv.s}%, ${color.hsv.v}%)`)}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Input
                  label="H"
                  type="number"
                  value={color.hsv.h}
                  readOnly
                />
                <Input
                  label="S"
                  type="number"
                  value={color.hsv.s}
                  readOnly
                />
                <Input
                  label="V"
                  type="number"
                  value={color.hsv.v}
                  readOnly
                />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                hsv({color.hsv.h}, {color.hsv.s}%, {color.hsv.v}%)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CMYK */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.colorConverter.cmyk')}</span>
              <Button
                onClick={() => copyToClipboard(`cmyk(${color.cmyk.c}%, ${color.cmyk.m}%, ${color.cmyk.y}%, ${color.cmyk.k}%)`)}
                size="sm"
                variant="outline"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <Input
                  label="C"
                  type="number"
                  value={color.cmyk.c}
                  readOnly
                />
                <Input
                  label="M"
                  type="number"
                  value={color.cmyk.m}
                  readOnly
                />
                <Input
                  label="Y"
                  type="number"
                  value={color.cmyk.y}
                  readOnly
                />
                <Input
                  label="K"
                  type="number"
                  value={color.cmyk.k}
                  readOnly
                />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                cmyk({color.cmyk.c}%, {color.cmyk.m}%, {color.cmyk.y}%, {color.cmyk.k}%)
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Color Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.colorConverter.colorInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.colorConverter.brightness')}</h4>
              <div className="space-y-1">
                <div>{t('tools.colorConverter.relativeLuminance')}: {((0.299 * color.rgb.r + 0.587 * color.rgb.g + 0.114 * color.rgb.b) / 255 * 100).toFixed(1)}%</div>
                <div>{t('tools.colorConverter.perceivedBrightness')}: {Math.sqrt(0.299 * color.rgb.r ** 2 + 0.587 * color.rgb.g ** 2 + 0.114 * color.rgb.b ** 2).toFixed(0)}</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.colorConverter.accessibility')}</h4>
              <div className="space-y-1">
                <div>
                  {t('tools.colorConverter.textOnWhite')}:
                  <span className={`ml-1 px-2 py-1 rounded text-xs ${
                    (0.299 * color.rgb.r + 0.587 * color.rgb.g + 0.114 * color.rgb.b) < 128
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {(0.299 * color.rgb.r + 0.587 * color.rgb.g + 0.114 * color.rgb.b) < 128 ? t('tools.colorConverter.good') : t('tools.colorConverter.poor')}
                  </span>
                </div>
                <div>
                  {t('tools.colorConverter.textOnBlack')}:
                  <span className={`ml-1 px-2 py-1 rounded text-xs ${
                    (0.299 * color.rgb.r + 0.587 * color.rgb.g + 0.114 * color.rgb.b) > 128
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {(0.299 * color.rgb.r + 0.587 * color.rgb.g + 0.114 * color.rgb.b) > 128 ? t('tools.colorConverter.good') : t('tools.colorConverter.poor')}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.colorConverter.cssUsage')}</h4>
              <div className="space-y-1 font-mono text-xs">
                <div>color: {color.hex};</div>
                <div>background: rgb({color.rgb.r}, {color.rgb.g}, {color.rgb.b});</div>
                <div>border: 1px solid {color.hex};</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Harmony */}
      <Card>
        <CardHeader>
          <CardTitle>Color Harmony</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.colorConverter.analogousColors')}</h4>
              <div className="flex gap-2">
                {generateAnalogousColors().map((analogousColor, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      updateColorFromHex(analogousColor);
                      addToHistory(analogousColor);
                    }}
                    className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                    style={{ backgroundColor: analogousColor }}
                    title={analogousColor}
                  />
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.colorConverter.triadicColors')}</h4>
              <div className="flex gap-2">
                {generateTriadicColors().map((triadicColor, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      updateColorFromHex(triadicColor);
                      addToHistory(triadicColor);
                    }}
                    className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                    style={{ backgroundColor: triadicColor }}
                    title={triadicColor}
                  />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saved Palette */}
      {savedPalette.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Saved Palette ({savedPalette.length})</span>
              <Button
                onClick={exportPalette}
                variant="outline"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-8 md:grid-cols-12 gap-2">
              {savedPalette.map((paletteColor, index) => (
                <div key={index} className="relative group">
                  <button
                    onClick={() => {
                      updateColorFromHex(paletteColor);
                      addToHistory(paletteColor);
                    }}
                    className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                    style={{ backgroundColor: paletteColor }}
                    title={paletteColor}
                  />
                  <button
                    onClick={() => removeFromPalette(paletteColor)}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('tools.colorConverter.removeFromPalette')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Color History */}
      {colorHistory.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.colorConverter.recentColors')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-10 md:grid-cols-20 gap-2">
              {colorHistory.slice(1).map((historyColor, index) => (
                <button
                  key={index}
                  onClick={() => updateColorFromHex(historyColor)}
                  className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                  style={{ backgroundColor: historyColor }}
                  title={historyColor}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
